import { UploadFile } from '@mui/icons-material';
import { Alert, FormControl, FormHelperText, Typography } from '@mui/material';
import { useCallback, useEffect, useState, CSSProperties } from 'react';
import { useDropzone, Accept, FileRejection } from 'react-dropzone';
import { useController, UseControllerProps } from 'react-hook-form';
import { toast } from 'react-toastify';

interface Props extends UseControllerProps {
  acceptedFormats: string;
  maxSizeBytes?: number;
  tooLargeMessage?: (file: File, sizeInMb: string) => string;
}

const parseAcceptedFormats = (formats: string): Accept => {
  const formatArray = formats.split(',').map(format => format.trim());
  const accept: Accept = formatArray.reduce((acc, format) => {
    const mimeType = getMimeType(format);
    if (mimeType) {
      acc[mimeType] = [];
    }
    return acc;
  }, {} as Accept);
  return accept;
};

const getMimeType = (extension: string): string | null => {
  const mimeTypes: { [key: string]: string } = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    pdf: 'application/pdf',
    zip: 'application/zip',
    // Dodaj inne potrzebne typy MIME tutaj
  };
  return mimeTypes[extension] || null;
};

export default function AppDropzone({ acceptedFormats, maxSizeBytes, tooLargeMessage, ...props }: Props) {
  const { fieldState, field } = useController({ ...props, defaultValue: null });
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 600);
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

  useEffect(() => {
    function handleResize() {
      setIsSmallScreen(window.innerWidth < 600);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dzStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    border: 'dashed 3px #eee',
    borderColor: '#eee',
    borderRadius: '5px',
    paddingTop: '30px',
    alignItems: 'center',
    height: '200px',
    width: isSmallScreen ? '100%' : '500px'
  };

  const dzActive: CSSProperties = {
    borderColor: 'green'
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    const file = acceptedFiles[0];

    try {
      setRejectionMessage(null);
      const preview = URL.createObjectURL(file);
      field.onChange(Object.assign(file, { preview }));
    } catch (error) {
      toast.error('Wystąpił błąd podczas przetwarzania pliku.');
      console.error('Error creating object URL:', error);
    }
  }, [field]);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    field.onChange(null);

    const rejection = fileRejections[0];
    const fileTooLarge = rejection?.errors.some(error => error.code === 'file-too-large');
    const invalidType = rejection?.errors.some(error => error.code === 'file-invalid-type');

    let message: string;

    if (fileTooLarge && rejection) {
      const sizeInMb = (rejection.file.size / (1024 * 1024)).toFixed(2);
      const sizeMessage = tooLargeMessage
        ? tooLargeMessage(rejection.file, sizeInMb)
        : `Plik „${rejection.file.name}” jest za duży (${sizeInMb} MB). Maksymalny rozmiar pliku to ${maxSizeBytes ? Math.floor(maxSizeBytes / (1024 * 1024)) : 50} MB. Wybierz mniejszy plik.`;
      message = invalidType
        ? `${sizeMessage} Plik ma również nieprawidłowy format. Akceptowane formaty: ${acceptedFormats}.`
        : sizeMessage;
    } else if (invalidType) {
      message = 'Nieprawidłowy format pliku. Akceptowane formaty: ' + acceptedFormats;
    } else {
      message = 'Nie udało się dodać pliku. Sprawdź jego format i rozmiar.';
    }

    setRejectionMessage(message);
    toast.error(message);
  }, [acceptedFormats, field, maxSizeBytes, tooLargeMessage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: parseAcceptedFormats(acceptedFormats),
    maxSize: maxSizeBytes,
    multiple: false,
    // VS Code WebView exposes the File System Access API but blocks getFile().
    // The native input fallback works in embedded and regular browsers.
    useFsAccessApi: false
  });

  return (
    <div>
      <div {...getRootProps()} style={{ display: 'flex', justifyContent: 'center' }}>
        <FormControl style={isDragActive ? { ...dzStyles, ...dzActive } : dzStyles} error={!!fieldState.error}>
          <input {...getInputProps()} />
          <UploadFile sx={{ fontSize: '100px' }} />
          <Typography variant='h4'>Przeciągnij plik</Typography>
          <FormHelperText>{fieldState.error?.message}</FormHelperText>
        </FormControl>
      </div>
      {rejectionMessage && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {rejectionMessage}
        </Alert>
      )}
    </div>
  );
}
